import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { mediaUrl } from '../utils/mediaUrl';
import { League, Team } from '../types';

interface StandingRow {
  team: Team; w: number; l: number; d: number; gf: number; ga: number; pts: number; gp: number;
}
interface PortalGame {
  id: string; title: string; sport: string; home_team: string; away_team: string;
  scheduled_at: string; status: string; home_final_score?: number; away_final_score?: number;
}

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const SPORT_EMOJI: Record<string, string> = { soccer: '⚽', baseball: '⚾', softball: '🥎', basketball: '🏀' };

export default function LeaguePage() {
  const { id } = useParams<{ id: string }>();
  const [league,    setLeague]    = useState<League | null>(null);
  const [teams,     setTeams]     = useState<Team[]>([]);
  const [games,     setGames]     = useState<PortalGame[]>([]);
  const [standings, setStandings] = useState<StandingRow[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [tab, setTab] = useState<'overview' | 'standings' | 'schedule'>('overview');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      api.get(`/leagues/${id}`),
      api.get(`/leagues/${id}/teams`),
      api.get(`/leagues/${id}/games`),
      api.get(`/leagues/${id}/standings`),
    ]).then(([lRes, tRes, gRes, sRes]) => {
      setLeague(lRes.data);
      setTeams(tRes.data);
      setGames(gRes.data);
      setStandings(sRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const upcoming = games
    .filter(g => g.status !== 'completed')
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  const past = games
    .filter(g => g.status === 'completed')
    .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());

  const card: React.CSSProperties = {
    background: 'var(--surface)', border: '1px solid var(--hairline)',
    borderRadius: '12px', padding: '16px 20px',
  };

  if (loading) return (
    <div className="container" style={{ paddingTop: 60, textAlign: 'center', color: 'var(--ink-4)' }}>
      Loading league…
    </div>
  );

  if (!league) return (
    <div className="container" style={{ paddingTop: 60, textAlign: 'center', color: 'var(--ink-4)' }}>
      League not found.
    </div>
  );

  const primaryColor = league.primary_color || '#F97316';

  return (
    <div className="container" style={{ paddingTop: 8, maxWidth: 860 }}>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <div style={{
        borderRadius: '16px', marginBottom: '24px',
        background: `linear-gradient(135deg, ${primaryColor}22, ${league.secondary_color || '#ffffff'}12)`,
        border: `1px solid ${primaryColor}30`, padding: '28px 28px 24px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          {/* Logo */}
          <div style={{
            width: 72, height: 72, borderRadius: '14px', flexShrink: 0,
            background: `${primaryColor}20`, border: `2px solid ${primaryColor}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '34px',
          }}>
            {league.logo_url
              ? <img src={mediaUrl(league.logo_url)} alt={league.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '12px' }} />
              : (SPORT_EMOJI[league.sport] || '🏟️')}
          </div>
          {/* Name + meta */}
          <div style={{ flex: 1, minWidth: '180px' }}>
            <h1 style={{ fontSize: '26px', fontWeight: 900, color: 'var(--ink-1)', marginBottom: '4px' }}>
              {league.name}
            </h1>
            <div style={{ fontSize: '14px', color: 'var(--ink-3)', marginBottom: '4px' }}>
              {SPORT_EMOJI[league.sport]}{' '}
              {league.sport.charAt(0).toUpperCase() + league.sport.slice(1)}
              {league.location ? ` · ${league.location}` : ''}
              {league.season_year ? ` · ${league.season_year} Season` : ''}
            </div>
            {league.description && (
              <div style={{ fontSize: '13px', color: 'var(--ink-3)', lineHeight: '1.5' }}>{league.description}</div>
            )}
          </div>
          {/* Stats + website */}
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
            {[{ v: teams.length, l: 'Teams' }, { v: games.length, l: 'Games' }].map(s => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 900, color: primaryColor, lineHeight: 1 }}>{s.v}</div>
                <div style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '2px' }}>{s.l}</div>
              </div>
            ))}
            {league.website_url && (
              <a href={league.website_url} target="_blank" rel="noopener noreferrer"
                style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, background: primaryColor, color: '#fff', textDecoration: 'none' }}>
                🌐 Website
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--hairline)', paddingBottom: '4px' }}>
        {([
          { id: 'overview',  label: '🏠 Overview' },
          { id: 'standings', label: '🏅 Standings' },
          { id: 'schedule',  label: `📅 Schedule (${games.length})` },
        ] as { id: typeof tab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 16px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            background: tab === t.id ? `${primaryColor}18` : 'transparent',
            color:      tab === t.id ? primaryColor : 'var(--ink-3)',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Overview ───────────────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
            Teams ({teams.length})
          </div>
          {teams.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: '40px', color: 'var(--ink-4)', marginBottom: '20px' }}>
              No teams in this league yet.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', marginBottom: '24px' }}>
              {teams.map(t => (
                <Link key={t.id} to={`/teams/${t.id}`} style={{ textDecoration: 'none' }}>
                  <div style={{ ...card, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: 38, height: 38, borderRadius: '8px', flexShrink: 0, background: `${t.primary_color}20`, border: `2px solid ${t.primary_color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                        {t.logo_url
                          ? <img src={mediaUrl(t.logo_url)} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '6px' }} />
                          : (SPORT_EMOJI[t.sport] || '🏆')}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--ink-3)', marginTop: '1px' }}>{t.sport} · {t.season_year}</div>
                      </div>
                      <span style={{ fontSize: '14px', color: 'var(--ink-4)', flexShrink: 0 }}>→</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {upcoming.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                Next Up
              </div>
              {upcoming.slice(0, 3).map(g => (
                <div key={g.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '20px' }}>{SPORT_EMOJI[g.sport] || '🏆'}</div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>{g.title}</div>
                    <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '2px' }}>{fmtDate(g.scheduled_at)}</div>
                  </div>
                  {g.status === 'live' && (
                    <span style={{ fontSize: '11px', fontWeight: 800, padding: '3px 9px', borderRadius: '20px', background: 'rgba(244,67,54,0.15)', color: '#F44336', border: '1px solid rgba(244,67,54,0.3)' }}>
                      🔴 LIVE
                    </span>
                  )}
                  <Link to={`/watch/${g.id}`} style={{ fontSize: '12px', fontWeight: 700, color: primaryColor, textDecoration: 'none', flexShrink: 0 }}>
                    Watch →
                  </Link>
                </div>
              ))}
              {upcoming.length > 3 && (
                <div style={{ marginTop: '8px' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setTab('schedule')}>
                    View all {upcoming.length} games →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Standings ──────────────────────────────────────────────────────── */}
      {tab === 'standings' && (
        <div style={card}>
          {standings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>
              Standings will appear once games are completed.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '480px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--hairline)' }}>
                    {['#', 'Team', 'GP', 'W', 'L', 'D', 'GF', 'GA', 'GD', 'Pts'].map(h => (
                      <th key={h} style={{ padding: '10px 8px', textAlign: h === 'Team' ? 'left' : 'center', fontSize: '10px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row, i) => {
                    const gd = row.gf - row.ga;
                    return (
                      <tr key={row.team.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, color: 'var(--ink-4)', fontSize: '12px' }}>{i + 1}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: row.team.primary_color, flexShrink: 0 }} />
                            <Link to={`/teams/${row.team.id}`} style={{ fontWeight: 700, color: 'var(--ink-1)', textDecoration: 'none' }}>
                              {row.team.name}
                            </Link>
                          </div>
                        </td>
                        {[row.gp, row.w, row.l, row.d, row.gf, row.ga].map((v, j) => (
                          <td key={j} style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--ink-3)' }}>{v}</td>
                        ))}
                        <td style={{ padding: '10px 8px', textAlign: 'center', color: gd > 0 ? '#22C55E' : gd < 0 ? '#F87171' : 'var(--ink-3)', fontWeight: gd !== 0 ? 700 : 400 }}>
                          {gd > 0 ? `+${gd}` : gd}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 900, fontSize: '15px', color: primaryColor }}>
                          {row.pts}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Schedule ───────────────────────────────────────────────────────── */}
      {tab === 'schedule' && (
        <div>
          {games.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>
              No games scheduled yet.
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                    Upcoming ({upcoming.length})
                  </div>
                  {upcoming.map(g => (
                    <div key={g.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '20px', flexShrink: 0 }}>{SPORT_EMOJI[g.sport] || '🏆'}</div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>{g.title}</div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '2px' }}>{fmtDate(g.scheduled_at)}</div>
                      </div>
                      {g.status === 'live' && (
                        <span style={{ fontSize: '11px', fontWeight: 800, padding: '3px 9px', borderRadius: '20px', background: 'rgba(244,67,54,0.15)', color: '#F44336', border: '1px solid rgba(244,67,54,0.3)', flexShrink: 0 }}>
                          🔴 LIVE
                        </span>
                      )}
                      <Link to={`/watch/${g.id}`} style={{ fontSize: '12px', fontWeight: 700, color: primaryColor, textDecoration: 'none', flexShrink: 0 }}>
                        Watch →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
                    Results ({past.length})
                  </div>
                  {past.map(g => (
                    <div key={g.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '20px', flexShrink: 0 }}>{SPORT_EMOJI[g.sport] || '🏆'}</div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink-1)' }}>
                          {g.title}
                          {g.home_final_score != null && g.away_final_score != null && (
                            <span style={{ fontWeight: 900, marginLeft: '10px', color: primaryColor }}>
                              {g.away_final_score}–{g.home_final_score}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '2px' }}>{fmtDate(g.scheduled_at)}</div>
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '12px', background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)', flexShrink: 0 }}>
                        Final
                      </span>
                      <Link to={`/watch/${g.id}`} style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink-3)', textDecoration: 'none', flexShrink: 0 }}>
                        Recap →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
